import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Chess, DEFAULT_POSITION} from "chess.js";
import ChessBoard from "../components/ChessBoard";
import Moves from "../components/Moves";
import PlayerStrip from "../components/PlayerStrip";
import GameOverModal from "../components/GameOverModal";
import ComputerSetupModal, {pickJumpstartPosition, readComputerSettings} from "../components/ComputerSetupModal";
import Modal from "../components/ui/Modal";
import Icon from "../components/ui/Icon";
import Avatar from "../components/ui/Avatar";
import {useUser} from "../UserContext";
import usePageTheme from "../utils/usePageTheme";
import {buildPositions} from "../utils/chessFunctions";
import {createEngine, levelInfo} from "../utils/engine";
import {getSharedEngine} from "../utils/sharedEngine";
import RookChat from "../components/rook/RookChat";
import useLinePreview from "../components/rook/useLinePreview";
import LinePreviewBar from "../components/rook/LinePreviewBar";
import {playMoveSound, playSound, useMuted} from "../utils/sounds";
import {saveComputerGame} from "../clients/ComputerGameClient";
import ChessClock from "../ChessClock";

// The game in progress is kept in the browser, so a reload (or a trip to another page) does not lose it
const SAVED_GAME_KEY = "chesscorner.computerGame";

const loadSavedGame = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(SAVED_GAME_KEY) || "null");
        if (saved && Array.isArray(saved.moves) && saved.myColor && saved.level) return saved;
    } catch (e) {
        // ignore
    }
    return null;
};

const saveGame = (game) => {
    try {
        localStorage.setItem(SAVED_GAME_KEY, JSON.stringify(game));
    } catch (e) {
        // ignore
    }
};

// `jumpstart` holds the filters a Jumpstart position was picked with, so "Play again" can find a similar one.
// `timeControl` is {baseMs, incrementMs}, or null for a game without a clock. `clock` is the time each side had left
// when the current turn began.
const newGame = ({level, color, initialFen, jumpstart, timeControl}) => ({
    id: Date.now(),
    level: Number(level),
    myColor: color === "random" ? (Math.random() < 0.5 ? "w" : "b") : color === "black" ? "b" : "w",
    initialFen: initialFen || DEFAULT_POSITION,
    moves: [],
    result: null,
    jumpstart: jumpstart || null,
    timeControl: timeControl || null,
    clock: timeControl ? {w: timeControl.baseMs, b: timeControl.baseMs} : null,
    startedAt: new Date().toISOString(),
    saved: false
});

// Same rule as online games: running out of time is a draw if the other side cannot checkmate (a lone king, or
// a king with a single knight or bishop)
const hasMatingMaterial = (chess, color) => {
    const pieces = chess.board().flat().filter((square) => square && square.color === color && square.type !== "k");
    if (pieces.length === 0) return false;
    return !(pieces.length === 1 && (pieces[0].type === "n" || pieces[0].type === "b"));
};

const formatTimeControl = (timeControl) => {
    if (!timeControl) return null;
    const minutes = timeControl.baseMs / 60000;
    return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)}+${Math.round(timeControl.incrementMs / 1000)}`;
};

const colorName = (color) => (color === "w" ? "white" : "black");

// Works out whether the position is over, and how
const resultFor = (chess, myColor) => {
    if (chess.isCheckmate()) {
        const loser = chess.turn();
        return {outcome: loser === myColor ? "loss" : "win", reason: "by checkmate"};
    }
    if (chess.isStalemate()) return {outcome: "draw", reason: "by stalemate"};
    if (chess.isInsufficientMaterial()) return {outcome: "draw", reason: "by insufficient material"};
    if (chess.isThreefoldRepetition()) return {outcome: "draw", reason: "by repetition"};
    if (chess.isDraw()) return {outcome: "draw", reason: "by the 50 move rule"};
    return null;
};

const RESULT_SOUNDS = {win: "victory", loss: "defeat", draw: "draw"};

const ComputerGame = () => {
    usePageTheme("game");
    const location = useLocation();
    const navigate = useNavigate();
    const user = useUser();
    const [muted, setMuted] = useMuted();

    // Start a new game when we come from the setup dialog, otherwise continue the saved one (or start a default one)
    const [game, setGame] = useState(() => {
        const request = location.state;
        if (request?.newGame) return newGame(request);
        const saved = loadSavedGame();
        if (saved) return saved;
        const settings = readComputerSettings();
        return newGame({level: settings.level, color: settings.color});
    });
    const [orientation, setOrientation] = useState(game.myColor === "w" ? "white" : "black");
    const [viewPly, setViewPly] = useState(null);
    const [thinking, setThinking] = useState(false);
    const [engineError, setEngineError] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [confirmResign, setConfirmResign] = useState(false);
    const [showSetup, setShowSetup] = useState(false);
    const [findingPosition, setFindingPosition] = useState(false);
    const [startError, setStartError] = useState(null);

    // A new game requested from the setup dialog while already on this page
    const handledRequest = useRef(location.state?.requestedAt || null);
    useEffect(() => {
        const request = location.state;
        if (!request?.newGame) return;
        if (request.requestedAt !== handledRequest.current) {
            handledRequest.current = request.requestedAt;
            startNewRef.current(request);
        }
        // Forget the request in the browser history, so a reload continues this game instead of starting over
        navigate(location.pathname, {replace: true, state: null});
    }, [location.state, location.pathname, navigate]);

    useEffect(() => saveGame(game), [game]);

    const level = levelInfo(game.level);
    const positions = useMemo(() => buildPositions(game.initialFen, game.moves), [game.initialFen, game.moves]);
    const livePosition = positions[positions.length - 1];
    const liveTurn = new Chess(livePosition.fen).turn();
    const isOver = !!game.result;
    const isViewingHistory = viewPly !== null && viewPly < game.moves.length;
    const shown = isViewingHistory ? positions[viewPly] : livePosition;
    const myTurn = !isOver && liveTurn === game.myColor;

    // ----- Rook, the coach -----
    // Rook talks about the position on the board (also when you look back at an earlier move). His lines are shown
    // on this board, which is paused until you close them.
    const preview = useLinePreview();
    const previewing = preview.active;
    const shownPly = isViewingHistory ? viewPly : game.moves.length;
    const rookPosition = useMemo(() => ({
        startFen: game.initialFen,
        moves: positions.slice(1, shownPly + 1).map((p) => p.move.from + p.move.to + (p.move.promotion || ""))
    }), [game.initialFen, positions, shownPly]);
    const closePreview = preview.close;
    useEffect(() => closePreview(), [game.id, closePreview]);

    // ----- The clock -----
    // The clocks start after the first move and pause while you are away from the page (another tab, or a reload).
    const timed = !!(game.timeControl && game.clock);
    const clockRunning = timed && !isOver && game.moves.length > 0;
    const [turnStart, setTurnStart] = useState(() => Date.now());
    const turnStartRef = useRef(turnStart);
    const hiddenAtRef = useRef(typeof document !== "undefined" && document.hidden ? Date.now() : null);
    const [pageHidden, setPageHidden] = useState(() => typeof document !== "undefined" && document.hidden);

    // Time used on the current turn, not counting time the page was hidden
    const elapsedThisTurn = useCallback(() => (hiddenAtRef.current ?? Date.now()) - turnStartRef.current, []);

    const beginTurn = useCallback(() => {
        const now = Date.now();
        turnStartRef.current = now;
        if (hiddenAtRef.current !== null) hiddenAtRef.current = now;
        setTurnStart(now);
    }, []);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                if (hiddenAtRef.current === null) hiddenAtRef.current = Date.now();
            } else if (hiddenAtRef.current !== null) {
                turnStartRef.current += Date.now() - hiddenAtRef.current;
                hiddenAtRef.current = null;
                setTurnStart(turnStartRef.current);
            }
            setPageHidden(document.hidden);
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    // ----- The engine -----
    const engineRef = useRef(null);
    useEffect(() => {
        const engine = createEngine();
        engineRef.current = engine;
        engine.start().catch((e) => {
            // A stopped engine (leaving the page) is not an error
            if (!e.stopped) setEngineError(e.message || "The chess engine could not be started.");
        });
        return () => engine.stop();
    }, []);

    // Each game gets an id, so an answer that arrives after a new game was started is thrown away
    const gameRef = useRef(game);
    gameRef.current = game;

    const finish = useCallback((result, changes = {}) => {
        const finished = {...result, finishedAt: new Date().toISOString()};
        setGame((g) => ({...g, ...changes, result: finished}));
        playSound(RESULT_SOUNDS[result.outcome]);
        setTimeout(() => setShowResult(true), 500);
    }, []);

    // Someone ran out of time
    const flag = useCallback((color) => {
        const current = gameRef.current;
        if (current.result) return;
        const chess = new Chess(current.initialFen);
        current.moves.forEach((san) => chess.move(san));
        const other = color === "w" ? "b" : "w";
        const result = hasMatingMaterial(chess, other)
            ? {outcome: color === current.myColor ? "loss" : "win", reason: "on time"}
            : {outcome: "draw", reason: "by timeout vs insufficient material"};
        finish(result, {clock: {...current.clock, [color]: 0}});
    }, [finish]);

    const applyMove = useCallback((move) => {
        const current = gameRef.current;
        if (current.result) return false;
        const chess = new Chess(current.initialFen);
        current.moves.forEach((san) => chess.move(san));
        const mover = chess.turn();
        let clock = current.clock;
        if (current.timeControl && clock && current.moves.length > 0) {
            const left = clock[mover] - elapsedThisTurn();
            if (left <= 0) {
                flag(mover);
                return false;
            }
            clock = {...clock, [mover]: Math.round(left + current.timeControl.incrementMs)};
        }
        let played;
        try {
            played = chess.move(move);
        } catch (e) {
            return false;
        }
        beginTurn();
        const nextGame = {...current, moves: [...current.moves, played.san], clock};
        gameRef.current = nextGame;
        setGame(nextGame);
        setViewPly(null);
        playMoveSound(played, chess.inCheck());
        const result = resultFor(chess, current.myColor);
        if (result) finish(result);
        return true;
    }, [finish, flag, beginTurn, elapsedThisTurn]);

    // Watch the clock of the side to move
    useEffect(() => {
        if (!clockRunning) return;
        const interval = setInterval(() => {
            const current = gameRef.current;
            if (!current.result && current.clock[liveTurn] - elapsedThisTurn() <= 0) flag(liveTurn);
        }, 100);
        return () => clearInterval(interval);
    }, [clockRunning, liveTurn, game.moves.length, flag, elapsedThisTurn]);

    // Finished games are saved to your profile when you are logged in. If saving fails (no connection), it is tried
    // again the next time this page is opened.
    const isLoggedIn = !!user?.isLoggedIn;
    const [saveError, setSaveError] = useState(null);
    useEffect(() => {
        if (!game.result || game.saved || !isLoggedIn) return;
        setSaveError(null);
        const finishedGame = game;
        saveComputerGame({
            clientGameId: String(finishedGame.id),
            level: finishedGame.level,
            playerColor: finishedGame.myColor,
            initialFen: finishedGame.initialFen,
            moves: finishedGame.moves,
            outcome: finishedGame.result.outcome,
            reason: finishedGame.result.reason,
            baseTimeMs: finishedGame.timeControl?.baseMs ?? null,
            incrementMs: finishedGame.timeControl?.incrementMs ?? null,
            gameStarted: finishedGame.startedAt,
            gameFinished: finishedGame.result.finishedAt
        })
            .then(() => setGame((g) => (g.id === finishedGame.id ? {...g, saved: true} : g)))
            .catch((e) => {
                // Not saved: say why. It is tried again the next time this page is opened.
                const status = e?.response?.status;
                setSaveError(status === 401
                    ? "Your login has expired, so this game was not saved. Log in again and open this page to save it."
                    : "This game could not be saved to your profile right now. It will be tried again next time you open this page.");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game.result, game.saved, game.id, isLoggedIn]);

    // Let the computer move whenever it is its turn
    useEffect(() => {
        if (isOver || liveTurn === game.myColor || engineError) return;
        let cancelled = false;
        const gameId = game.id;
        const fen = livePosition.fen;
        setThinking(true);
        const startedAt = Date.now();
        // With a clock, the computer spends its time sensibly: never more than its level allows, and less when low
        let searchLevel = level;
        let pause = 450 + Math.random() * 350;
        const current = gameRef.current;
        if (current.timeControl && current.clock) {
            const left = current.clock[liveTurn];
            const budget = Math.min(level.movetime, left / 40 + current.timeControl.incrementMs * 0.5);
            searchLevel = {...level, movetime: Math.max(30, Math.round(budget))};
            pause = Math.min(pause, left / 80);
        }
        const chooseMove = async () => {
            const legal = new Chess(fen).moves({verbose: true});
            if (level.randomness && Math.random() < level.randomness) {
                const random = legal[Math.floor(Math.random() * legal.length)];
                return {from: random.from, to: random.to, promotion: random.promotion};
            }
            // The whole game, not just the current position, so the computer knows about repetitions
            const history = new Chess(current.initialFen);
            const uciMoves = current.moves.map((san) => {
                const m = history.move(san);
                return m.from + m.to + (m.promotion || "");
            });
            const uci = await engineRef.current.bestMove({startFen: current.initialFen, moves: uciMoves}, searchLevel);
            if (!uci) return null;
            return {from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.length > 4 ? uci[4] : undefined};
        };
        chooseMove()
            .then((move) => {
                // A short pause, so the computer does not answer the very instant you let go of a piece
                const wait = Math.max(0, pause - (Date.now() - startedAt));
                setTimeout(() => {
                    if (cancelled || gameRef.current.id !== gameId || gameRef.current.result) return;
                    setThinking(false);
                    if (move) applyMove(move);
                }, wait);
            })
            .catch((e) => {
                if (cancelled || e.stopped) return;
                setThinking(false);
                setEngineError(e.message || "The chess engine stopped answering.");
            });
        return () => {
            cancelled = true;
            setThinking(false);
        };
    }, [game.id, game.moves.length, isOver, liveTurn, game.myColor, engineError, livePosition.fen, level, applyMove]);

    const handleMove = (move) => {
        if (!myTurn || thinking || isViewingHistory) return;
        applyMove({from: move.from, to: move.to, promotion: move.promotion});
    };

    // ----- Actions -----
    const startNew = (options = {}) => {
        const next = newGame({
            level: game.level,
            color: colorName(game.myColor),
            timeControl: game.timeControl,
            ...options
        });
        gameRef.current = next;
        beginTurn();
        setEngineError(null);
        setStartError(null);
        setGame(next);
        setViewPly(null);
        setShowResult(false);
        setThinking(false);
        setOrientation(next.myColor === "w" ? "white" : "black");
        playSound("start");
    };

    // "Play again": the same level and colour, or for Jumpstart a new position picked with the same settings
    const playAgain = async () => {
        if (!game.jumpstart) {
            startNew();
            return;
        }
        setFindingPosition(true);
        setStartError(null);
        try {
            const position = await pickJumpstartPosition(game.jumpstart);
            startNew({color: position.color, initialFen: position.fen, jumpstart: game.jumpstart});
        } catch (e) {
            setShowResult(false);
            setStartError(e.message || "Could not find a position. Try again in a moment.");
        } finally {
            setFindingPosition(false);
        }
    };

    const startNewRef = useRef(startNew);
    startNewRef.current = startNew;

    // Takes back your last move (and the computer's answer to it)
    const iStarted = new Chess(game.initialFen).turn() === game.myColor;
    const myMoveCount = iStarted ? Math.ceil(game.moves.length / 2) : Math.floor(game.moves.length / 2);
    // Not in games with a clock, where it would make the clock meaningless
    const canTakeBack = !timed;
    const takeBack = () => {
        if (!canTakeBack || isOver || thinking || myMoveCount === 0) return;
        const lastMoverIsMe = (game.moves.length % 2 === 1) === iStarted;
        const remove = lastMoverIsMe ? 1 : 2;
        const keep = Math.max(0, game.moves.length - remove);
        const next = {...game, moves: game.moves.slice(0, keep)};
        gameRef.current = next;
        setGame(next);
        setViewPly(null);
    };

    const resign = () => {
        setConfirmResign(false);
        const current = gameRef.current;
        const clock = clockRunning
            ? {...current.clock, [liveTurn]: Math.max(0, Math.round(current.clock[liveTurn] - elapsedThisTurn()))}
            : current.clock;
        finish({outcome: "loss", reason: "by resignation"}, {clock});
    };

    const goToAnalysis = () => {
        navigate("/analysis", {
            state: {
                initialFen: game.initialFen,
                moves: game.moves,
                playerColor: colorName(game.myColor),
                opponent: {username: `Computer (level ${game.level})`, isActualUser: false}
            }
        });
    };

    // ----- Stepping through the moves -----
    const viewPlyRef = useRef(viewPly);
    viewPlyRef.current = viewPly;
    const goToPly = useCallback((ply, silent = false) => {
        const total = gameRef.current.moves.length;
        const clamped = Math.max(0, Math.min(ply, total));
        const next = clamped >= total ? null : clamped;
        if (next === viewPlyRef.current) return;
        viewPlyRef.current = next;
        setViewPly(next);
        if (silent) return;
        const position = buildPositions(gameRef.current.initialFen, gameRef.current.moves.slice(0, clamped));
        const last = position[position.length - 1];
        if (last.move) playMoveSound(last.move, last.inCheck);
    }, []);

    useEffect(() => {
        const handleKey = (event) => {
            const tag = event.target.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable) return;
            const total = gameRef.current.moves.length;
            const current = viewPlyRef.current === null ? total : viewPlyRef.current;
            if (event.key === "ArrowLeft") goToPly(current - 1, event.repeat);
            else if (event.key === "ArrowRight") goToPly(current + 1, event.repeat);
            else if (event.key === "Home") goToPly(0);
            else if (event.key === "End") goToPly(total);
            else if (event.key === "f") setOrientation((o) => (o === "white" ? "black" : "white"));
            else return;
            event.preventDefault();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [goToPly]);

    // ----- Layout -----
    const computer = {username: "Computer", isActualUser: false};
    const computerTag = thinking && !isOver
        ? <span className="chip">Thinking…</span>
        : <span className="chip">Level {game.level}</span>;
    const me = user?.username ? {...user, isActualUser: user.isLoggedIn} : {username: "You", isActualUser: false};
    const topColor = orientation === "white" ? "b" : "w";
    const bottomColor = orientation === "white" ? "w" : "b";
    const playerFor = (color) => (color === game.myColor ? me : computer);
    const lastMove = shown.move ? {from: shown.move.from, to: shown.move.to} : null;

    const turnText = isOver
        ? "Game over"
        : engineError ? "The computer is unavailable"
            : thinking || !myTurn ? "The computer is thinking…" : "Your move";
    const clockFor = (color) => timed ? (
        <ChessClock
            timeLeft={game.clock[color]}
            syncedAt={turnStart}
            running={clockRunning && !pageHidden && liveTurn === color}
            warn={color === game.myColor}
        />
    ) : null;

    const subText = isOver
        ? `${game.result.outcome === "win" ? "You won" : game.result.outcome === "loss" ? "You lost" : "Draw"} ${game.result.reason}`
        : `${game.jumpstart ? "Jumpstart: you" : "You"} play ${colorName(game.myColor)} against ${level.name.toLowerCase()} (level ${game.level})`;

    return (
        <div className="page">
            <div className="game-layout">
                <div className="game-layout__board">
                    <div className="board-column">
                        <PlayerStrip user={playerFor(topColor)} color={topColor} fen={shown.fen} isYou={topColor === game.myColor}
                                     tag={topColor !== game.myColor ? computerTag : null} clock={clockFor(topColor)}/>
                        <div style={{position: "relative"}}>
                            <ChessBoard
                                id={`computer-${game.id}`}
                                fen={previewing ? preview.fen : shown.fen}
                                orientation={orientation}
                                onMove={handleMove}
                                canMove={!isOver && !isViewingHistory && !previewing}
                                locked={previewing}
                                previewing={previewing}
                                annotations={preview.annotations}
                                playerColor={game.myColor}
                                premoves
                                lastMove={previewing ? preview.lastMove : lastMove}
                            />
                            {isViewingHistory && !previewing && (
                                <div className="history-banner">
                                    Viewing move {viewPly} of {game.moves.length}
                                    <button className="btn btn-sm" onClick={() => setViewPly(null)}>Back to the game</button>
                                </div>
                            )}
                        </div>
                        <PlayerStrip user={playerFor(bottomColor)} color={bottomColor} fen={shown.fen} isYou={bottomColor === game.myColor}
                                     tag={bottomColor !== game.myColor ? computerTag : null} clock={clockFor(bottomColor)}/>
                        <LinePreviewBar preview={preview}/>
                    </div>
                </div>

                <aside className="game-layout__panel">
                    <div className="card panel">
                        <div className="game-status">
                            {thinking && !isOver ? <span className="spinner spinner--sm"/> :
                                <span className={`turn-dot turn-dot--${isOver ? game.myColor : liveTurn}`}/>}
                            <div className="game-status__text">
                                <div>{turnText}</div>
                                <div className="game-status__sub">{subText}</div>
                            </div>
                            <button className="icon-btn" onClick={() => setMuted(!muted)}
                                    aria-label={muted ? "Turn sound on" : "Mute sound"} title={muted ? "Sound off" : "Sound on"}>
                                <Icon name={muted ? "volumeOff" : "volume"}/>
                            </button>
                        </div>
                        {engineError && (
                            <div className="panel__section" style={{paddingTop: 0}}>
                                <div className="form-error engine-error">
                                    <span>{engineError} If it keeps happening, try reloading the page.</span>
                                    <button className="btn btn-sm" onClick={() => setEngineError(null)}>
                                        <Icon name="refresh"/> Try again
                                    </button>
                                </div>
                            </div>
                        )}
                        <div style={{borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", minHeight: 0, flex: 1}}>
                            <Moves
                                moves={game.moves}
                                initialFen={game.initialFen}
                                currentPly={isViewingHistory ? viewPly : game.moves.length}
                                onSelect={goToPly}
                                emptyText={iStarted ? "No moves yet. You start!" : "The computer starts."}
                                extraControls={
                                    <button className="icon-btn" onClick={() => setOrientation((o) => (o === "white" ? "black" : "white"))}
                                            aria-label="Flip board" title="Flip board (F)">
                                        <Icon name="flip"/>
                                    </button>
                                }
                            />
                        </div>
                        <div className="panel__section">
                            {!isOver ? (
                                <div className="action-row">
                                    {canTakeBack && (
                                        <button className="btn" onClick={takeBack} disabled={thinking || myMoveCount === 0}
                                                title="Take back your last move">
                                            <Icon name="undo"/> Take back
                                        </button>
                                    )}
                                    <button className="btn btn-danger" onClick={() => setConfirmResign(true)}>
                                        <Icon name="flag"/> Resign
                                    </button>
                                </div>
                            ) : (
                                <div className="stack stack--sm">
                                    <button className="btn btn-primary btn-block" onClick={playAgain} disabled={findingPosition}>
                                        {findingPosition ? <span className="spinner spinner--sm"/> : <Icon name="refresh"/>}
                                        {findingPosition ? "Finding a position…" : "Play again"}
                                    </button>
                                    {startError && <div className="form-error">{startError}</div>}
                                    <div className="action-row">
                                        <button className="btn" onClick={goToAnalysis}><Icon name="sparkles"/> Review</button>
                                        <button className="btn" onClick={() => setShowResult(true)}><Icon name="trophy"/> Result</button>
                                    </div>
                                    <p className="small muted save-note">
                                        {!isLoggedIn ? "Log in to keep your games against the computer on your profile."
                                            : game.saved ? "Saved to your profile."
                                                : saveError || "Saving to your profile…"}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>

                <aside className="game-layout__side stack">
                    <div className="card card-pad stack stack--sm">
                        <div className="versus">
                            <Avatar name="Computer" size={40} guest/>
                            <div style={{minWidth: 0}}>
                                <div className="small muted">Playing against</div>
                                <div style={{fontWeight: 800}}>The computer</div>
                            </div>
                        </div>
                        <div className="computer-level">
                            <div className="computer-level__top">
                                <strong>Level {game.level}: {level.name}</strong>
                                <span className="small muted">{level.blurb}</span>
                            </div>
                            <div className="computer-level__bar" aria-hidden="true">
                                {Array.from({length: 8}, (_, i) => <span key={i} className={i < game.level ? "on" : ""}/>)}
                            </div>
                        </div>
                        <p className="small muted" style={{margin: 0}}>
                            {timed ? `${formatTimeControl(game.timeControl)} clock, no rating. Takebacks are off in games with a clock.`
                                : "No clock and no rating. Take your time."}
                        </p>
                        <button className="btn btn-block" onClick={() => setShowSetup(true)}>
                            <Icon name="sliders"/> New game or level
                        </button>
                    </div>
                    <div className="card rook-card">
                        <RookChat position={rookPosition} mode="computer" playerColor={game.myColor}
                                  getEngine={getSharedEngine} preview={preview} storeKey={`computer-${game.id}`}/>
                    </div>
                </aside>
            </div>

            {confirmResign && (
                <Modal size="sm" title="Resign this game?" onClose={() => setConfirmResign(false)}
                       footer={<>
                           <button className="btn" onClick={() => setConfirmResign(false)}>Keep playing</button>
                           <button className="btn btn-danger" onClick={resign}><Icon name="flag"/> Resign</button>
                       </>}>
                    <div className="modal-body">
                        <p className="muted">The computer will be given the win.</p>
                    </div>
                </Modal>
            )}

            {showResult && game.result && (
                <GameOverModal
                    outcome={game.result.outcome}
                    reason={game.result.reason}
                    opponent={{username: `The computer, level ${game.level}`, isActualUser: false}}
                    onClose={() => setShowResult(false)}
                    onAnalysis={goToAnalysis}
                    onHome={() => navigate("/")}
                    onOfferRematch={playAgain}
                    rematchLabel="Play again"
                />
            )}

            <ComputerSetupModal open={showSetup} onClose={() => setShowSetup(false)}/>
        </div>
    );
};

export default ComputerGame;
